import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon, 
  CurrencyDollarIcon,
  ChartBarIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import api from '../api/client';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function RiskDashboard() {
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRiskData();
  }, []);

  const loadRiskData = async () => {
    try {
      // Try to get actual risk assessment data
      const data = await api.assessRisk();
      setRiskData(data);
    } catch (error) {
      console.error('Failed to load risk data:', error);
      // Use demo data for display
      setRiskData(getDemoData());
    } finally {
      setLoading(false);
    }
  };

  const getDemoData = () => ({
    total_eal: 23532075,
    confidence_interval: {
      lower: 16613644.95,
      upper: 30450505.05,
      confidence_level: 95
    },
    hub_criticality: [
      { asset: 'customer-data-prod', score: 0.962, eal: 8500000 },
      { asset: 'i-app-01', score: 0.932, eal: 7200000 },
      { asset: 'i-web-01', score: 0.447, eal: 2800000 }
    ],
    vulnerability_breakdown: {
      critical: 2,
      high: 5,
      medium: 12,
      low: 23
    },
    attack_simulations: {
      script_kiddie: 12000,
      criminal_group: 45000,
      apt: 78000,
      insider: 65000
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const data = riskData || getDemoData();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Vulnerability breakdown chart
  const vulnChartData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [{
      data: [
        data.vulnerability_breakdown?.critical || 0,
        data.vulnerability_breakdown?.high || 0,
        data.vulnerability_breakdown?.medium || 0,
        data.vulnerability_breakdown?.low || 0
      ],
      backgroundColor: [
        'rgba(239, 68, 68, 0.8)',
        'rgba(249, 115, 22, 0.8)',
        'rgba(234, 179, 8, 0.8)',
        'rgba(34, 197, 94, 0.8)'
      ],
      borderColor: [
        'rgb(239, 68, 68)',
        'rgb(249, 115, 22)',
        'rgb(234, 179, 8)',
        'rgb(34, 197, 94)'
      ],
      borderWidth: 2,
    }]
  };

  // Hub criticality chart
  const hubChartData = {
    labels: data.hub_criticality?.map(h => h.asset) || [],
    datasets: [{
      label: 'Criticality Score',
      data: data.hub_criticality?.map(h => h.score) || [],
      backgroundColor: 'rgba(14, 165, 233, 0.8)',
      borderColor: 'rgb(14, 165, 233)',
      borderWidth: 2,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: 'rgb(209, 213, 219)'
        }
      }
    },
    scales: {
      y: {
        ticks: { color: 'rgb(156, 163, 175)' },
        grid: { color: 'rgba(75, 85, 99, 0.3)' }
      },
      x: {
        ticks: { color: 'rgb(156, 163, 175)' },
        grid: { color: 'rgba(75, 85, 99, 0.3)' }
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Main EAL Card */}
      <div className="bg-gradient-to-br from-danger-900 to-danger-800 rounded-lg p-8 border border-danger-700">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <CurrencyDollarIcon className="h-6 w-6 text-danger-300" />
              <h2 className="text-lg font-semibold text-danger-100">Expected Annual Loss (EAL)</h2>
            </div>
            <p className="text-5xl font-bold text-white mb-4">
              {formatCurrency(data.total_eal)}
            </p>
            <div className="text-sm text-danger-200">
              <p>95% Confidence Interval:</p>
              <p className="font-mono mt-1">
                {formatCurrency(data.confidence_interval?.lower)} - {formatCurrency(data.confidence_interval?.upper)}
              </p>
            </div>
          </div>
          <div className="bg-danger-800/50 p-4 rounded-lg">
            <ExclamationTriangleIcon className="h-12 w-12 text-danger-300" />
          </div>
        </div>
        
        <div className="mt-6 pt-6 border-t border-danger-700">
          <p className="text-sm text-danger-200">
            Based on 100,000 Monte Carlo simulations across 4 attacker profiles
          </p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">Critical Assets</h3>
            <ShieldExclamationIcon className="h-5 w-5 text-primary-400" />
          </div>
          <p className="text-3xl font-bold text-white">
            {data.hub_criticality?.filter(h => h.score > 0.9).length || 2}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Hub criticality score &gt; 0.9
          </p>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">High/Critical Findings</h3>
            <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
          </div>
          <p className="text-3xl font-bold text-white">
            {(data.vulnerability_breakdown?.critical || 0) + (data.vulnerability_breakdown?.high || 0)}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Require immediate attention
          </p>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-400">Attack Simulations</h3>
            <ChartBarIcon className="h-5 w-5 text-green-400" />
          </div>
          <p className="text-3xl font-bold text-white">100K</p>
          <p className="text-sm text-gray-400 mt-2">
            Monte Carlo iterations
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vulnerability Breakdown */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Vulnerability Breakdown</h3>
          <div className="h-64">
            <Doughnut data={vulnChartData} options={{ ...chartOptions, plugins: { legend: { position: 'bottom', labels: { color: 'rgb(209, 213, 219)' } } } }} />
          </div>
        </div>

        {/* Hub Criticality */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Hub Criticality Analysis</h3>
          <div className="h-64">
            <Bar data={hubChartData} options={{ ...chartOptions, indexAxis: 'y' }} />
          </div>
        </div>
      </div>

      {/* Critical Assets Table */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">Critical Assets</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Asset</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Criticality Score</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Estimated EAL</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.hub_criticality?.map((asset, index) => (
                <tr key={index} className="border-b border-gray-700 hover:bg-gray-750">
                  <td className="py-3 px-4 text-white font-mono text-sm">{asset.asset}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            asset.score > 0.9 ? 'bg-danger-500' : asset.score > 0.7 ? 'bg-orange-500' : 'bg-yellow-500'
                          }`}
                          style={{ width: `${asset.score * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-300">{asset.score.toFixed(3)}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-white">{formatCurrency(asset.eal)}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      asset.score > 0.9 ? 'bg-danger-900 text-danger-300' : 'bg-orange-900 text-orange-300'
                    }`}>
                      {asset.score > 0.9 ? 'CRITICAL' : 'HIGH'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
